/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { useRef } from "react"
import { useSkeem } from "../providers/SkeemProvider"
import { useHistory } from "react-router-dom"
import { TextGroupMedium } from "./Title"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { IRecord } from "../types/schema"
import { CGButton } from "./CGButton"
import { Form2 } from "@cd2/forms/build/es6"
import { SubmitButton } from "./SubmitButton"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { IndividualUserTermsPage } from "../pages/user/IndividualUserTermsPage"
import { useDialogueConfirmation2 } from "../hooks/useDialogConfirmation"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { ButtonGrid } from "../pages/employees/EmployeesTasksPage"

type IUser = IRecord<"skeem_users", "termsAndConditions">

export function TermsAndConditionsPopup({ onAcceptTerms }: { onAcceptTerms: () => void }) {
  const skeem = useSkeem()
  const history = useHistory()

  const meRef = useRef<IUser | null>(null)

  useAsyncEffect(async () => {
    let currentMe = meRef.current

    if (!currentMe) {
      const me = await skeem.fetchMe<IUser>(["termsAndConditions"])
      meRef.current = me
      currentMe = me
    }

    if (currentMe === null) {
      console.warn("terms and conditions popup rendered with no authed user")
      return
    }

    if (currentMe.termsAndConditions) {
      return
    }

    const didAccept = await triggerShowModal({ userId: currentMe.id })
    if (!didAccept) return
    currentMe.termsAndConditions = true
    meRef.current = currentMe
    onAcceptTerms()
  }, [history.location.pathname])

  const triggerShowModal = useTermsAndConditionsDialog()

  return null
}

function useTermsAndConditionsDialog() {
  return useDialogueConfirmation2<boolean, { userId: string }>({
    width: 800,
    Component({ dialogData, onResolve }) {
      const siteAlerts = useSiteAlerts()

      const handleSave = useAsyncCallback(async () => {
        await window.skeem.mutate({
          skeem_users: {
            update: {
              id: dialogData.userId,
              termsAndConditions: true,
            },
          },
        })
        onResolve(true)
        siteAlerts.termsAccepted()
      })

      return (
        <Form2.Form onSubmit={handleSave}>
          <TextGroupMedium
            heading={`Welcome to CleverGoose`}
            body={`You must accept the User Agreement to continue using this platform.`}
          />
          <div
            css={css`
              overflow: auto;
              border: 1px solid #ddd;
              background: #f5f5f5;
              padding: 8px 16px;
              box-shadow: inset 0 0 4px rgba(0, 0, 0, 0.25);
            `}
          >
            <IndividualUserTermsPage />
          </div>
          <ButtonGrid>
            <SubmitButton value="Accept" />
            <CGButton
              buttonColor="white"
              onClick={() => {
                window.skeem.logout()
                siteAlerts.termsDeclined()
                setTimeout(() => {
                  window.location.href = "/"
                }, 500)
              }}
            >
              Decline
            </CGButton>
          </ButtonGrid>
        </Form2.Form>
      )
    },
  })
}
